// Oliver Sweeney — Global components (runs on every page)
// Depends on: hc-sticky.js, dom-slider.js, choices.min.js, swiper-bundle.min.js

// ─── CartCount ────────────────────────────────────────────────────────────────
class CartCount {
  constructor(elem) {
    this._elem = elem
  }
  onInit() {
    this._elem.addEventListener('cartItemAdded', () => {}) // fallback
    document.addEventListener('cartItemAdded', e => {
      const cart = e.detail.data
      this._elem.classList.add('items-in-cart')
      this._elem.querySelector('span').textContent = cart.item_count
    })
  }
}

// ─── ChangeClass ──────────────────────────────────────────────────────────────
class ChangeClass {
  constructor(elem) {
    this._elem      = elem
    this._instances = JSON.parse(elem.getAttribute('data-change-class') || '{}')
    this._defaults  = {
      action: 'toggle', class: 'active', delay: 0, elem: 'self',
      maxWidth: 9999999, minWidth: 0, targetOnly: false,
      preventDefault: false, event: 'click', focus: false, focusElem: ''
    }
  }
  onInit() {
    if (!this._instances.length) return
    this._instances.forEach(instance => {
      instance = { ...this._defaults, ...instance }
      this._elem.addEventListener(instance.event, e => {
        if (window.innerWidth > instance.maxWidth || window.innerWidth < instance.minWidth) return
        if (instance.targetOnly && e.target !== this._elem) return
        if (instance.preventDefault) e.preventDefault()
        const el = this._getElement(instance.elem)
        if (!el) return
        setTimeout(() => {
          this._changeClass(el, instance.class, instance.action, instance.focus, instance.focusElem)
          if (instance.removeFromSiblings) this._removeFromSiblings(el, instance.class, instance.removeFromSiblings)
        }, instance.delay)
      })
    })
  }
  _getElement(ref) {
    switch (ref) {
      case 'self':   return this._elem
      case 'parent': return this._elem.parentNode
      case 'next':   return this._elem.nextElementSibling
      case 'prev':   return this._elem.previousElementSibling
      default:       return document.querySelector(ref)
    }
  }
  _changeClass(el, cls, action, focus, focusElem) {
    if (el.classList.contains(cls)) {
      if (action !== 'add') el.classList.remove(cls)
    } else {
      if (action !== 'remove') {
        el.classList.add(cls)
        if (focus && focusElem) document.querySelector(focusElem)?.focus()
      }
    }
  }
  _removeFromSiblings(el, cls, data) {
    const parent   = data.parentSiblings ? el.parentNode.parentNode : el.parentNode
    const siblings = data.selector ? parent.querySelectorAll(data.selector) : [...parent.children]
    siblings.forEach(s => { if (s !== el) s.classList.remove(cls) })
  }
}

// ─── Currency ─────────────────────────────────────────────────────────────────
class Currency {
  constructor(elem) {
    this._select = elem.querySelector('select')
    this._form   = elem.querySelector('form')
  }
  onInit() {
    this._select.addEventListener('change', () => this._form.submit())
  }
}

// ─── InViewport ───────────────────────────────────────────────────────────────
class InViewport {
  constructor(elem) {
    this._elem   = elem
    this._target = elem.getAttribute('data-in-viewport') || null
  }
  onInit() {
    this._check()
    document.addEventListener('scroll', () => this._check(), { passive: true })
  }
  _check() {
    const bb     = this._elem.getBoundingClientRect()
    const t      = this._target ? document.querySelector(this._target) : this._elem
    if (!t) return
    t.classList.toggle('in-viewport',      bb.top < window.innerHeight)
    t.classList.toggle('been-in-viewport', bb.bottom < 0)
  }
}

// ─── ScrollTo ─────────────────────────────────────────────────────────────────
class ScrollTo {
  constructor(elem) {
    this._elem = elem
    this._data = {
      targetElem: 'html', offset: 0, mobOffset: 0, duration: 750, header: false,
      ...JSON.parse(elem.getAttribute('data-scroll-to') || '{}')
    }
  }
  onInit() {
    if (this._data.header) {
      const h = document.querySelector('header')
      this._data.offset = this._data.mobOffset = h ? -h.offsetHeight : 0
    }
    this._elem.addEventListener('click', () => {
      const offset = window.innerWidth > 1024 ? this._data.offset : this._data.mobOffset
      window.OSHelpers?.scrollTo(this._data.targetElem, offset)
    })
  }
}

// ─── Sticky ───────────────────────────────────────────────────────────────────
class Sticky {
  constructor(elem) {
    this._elem    = elem
    this._options = {
      top: 0, bottomEnd: 0, innerTop: 0, followScroll: false,
      desktopOnly: false, header: false,
      stickTo: elem.closest('.sticky-container') || elem.parentNode,
      ...JSON.parse(elem.dataset.sticky || '{}')
    }
    if (this._options.desktopOnly) {
      this._options.responsive = { 1024: { disable: true } }
    }
    if (this._options.header) {
      const h = document.querySelector('header')
      if (h) this._options.top = h.offsetHeight
    }
  }
  onInit() {
    if (!window.hcSticky) return
    const sticky = new window.hcSticky(this._elem, this._options)
    if (this._options.header) {
      window.addEventListener('resize', () => {
        const h = document.querySelector('header')
        if (h) sticky.update({ top: h.offsetHeight })
      })
    }
  }
}

// ─── Slide ────────────────────────────────────────────────────────────────────
class Slide {
  constructor(elem) {
    this._elem = elem
    this._data = {
      elemToChange: 'next', action: 'toggle', delay: 0,
      maxWidth: 99999999, minWidth: 0,
      ...JSON.parse(elem.getAttribute('data-slide') || '{}')
    }
  }
  onInit() {
    this._elem.addEventListener('click', () => {
      if (window.innerWidth > this._data.maxWidth || window.innerWidth < this._data.minWidth) return
      const el = this._getElement(this._data.elemToChange)
      if (!el) return
      this._slide(el, this._data.action)
      if (this._data.closeSiblings) this._closeSiblings(el)
      const vid = el.querySelector('video')
      if (vid) {
        vid.pause()
        el.querySelector('[data-video-play-button]')?.classList.remove('visually-hidden')
      }
      const iframe = el.querySelector('iframe')
      if (iframe) {
        const src = iframe.getAttribute('src')
        iframe.removeAttribute('src')
        iframe.setAttribute('src', src)
      }
    })
  }
  _getElement(ref) {
    switch (ref) {
      case 'next':   return this._elem.nextElementSibling
      case 'prev':   return this._elem.previousElementSibling
      case 'parent': return this._elem.parentNode
      default:       return document.querySelector(ref)
    }
  }
  _slide(el, action) {
    if (!window.domSlider) return
    const { slideDown, slideUp, slideToggle } = window.domSlider
    const delay = this._data.delay
    if (action === 'show')      slideDown({ element: el, delay })
    else if (action === 'hide') slideUp({ element: el, delay })
    else                        slideToggle({ element: el, delay })
  }
  _closeSiblings(el) {
    if (!window.domSlider) return
    const { slideUp } = window.domSlider
    document.querySelectorAll(this._data.closeSiblings).forEach(s => {
      if (s === el) return
      const target = this._data.closeSiblingsElement ? s.querySelector(this._data.closeSiblingsElement) : s
      slideUp({ element: target })
    })
  }
}

// ─── Video ────────────────────────────────────────────────────────────────────
class Video {
  constructor(elem) {
    this._elem          = elem
    this._video         = elem.querySelector('video')
    this._pause         = elem.querySelector('[data-pause-video]')
    this._play          = elem.querySelector('[data-play-video]')
    this._progressEl    = elem.querySelector('[data-progress]')
    this._circle        = null
    this._circumference = 0
  }
  onInit() {
    if (this._progressEl) {
      this._circle        = this._progressEl.querySelector('circle')
      const r             = this._circle.getAttribute('r')
      this._circumference = r * 2 * Math.PI
      this._circle.style.strokeDasharray  = `${this._circumference} ${this._circumference}`
      this._circle.style.strokeDashoffset = this._circumference
    }
    if (this._elem.getAttribute('data-self-trigger')) {
      this._elem.addEventListener('click', e => {
        if (e.target.nodeName === 'VIDEO' || e.target === this._elem) {
          this._video.paused ? this._doPlay() : this._doPause()
        }
      })
    }
    if (this._play) {
      this._play.addEventListener('click',           () => this._doPlay())
      this._video.addEventListener('ended',          () => this._ended())
      this._video.addEventListener('timeupdate',     () => this._setProgress())
    }
    if (this._pause) {
      this._pause.addEventListener('click',          () => this._doPause())
    }
    this._video.addEventListener('pauseVideo',       () => this._doPause())
  }
  _doPlay() {
    this._play?.classList.add('visually-hidden')
    this._pause?.classList.remove('visually-hidden')
    this._video.play()
  }
  _doPause() {
    this._video.pause()
    this._play?.classList.remove('visually-hidden')
    this._pause?.classList.add('visually-hidden')
  }
  _ended() {
    this._play?.classList.remove('visually-hidden')
    this._pause?.classList.add('visually-hidden')
    this._video.load()
  }
  _setProgress() {
    if (!this._circle) return
    const pct    = (100 / this._video.duration) * this._video.currentTime
    this._circle.style.strokeDashoffset = this._circumference - pct / 100 * this._circumference
  }
}

// ─── Preload ─────────────────────────────────────────────────────────────────
class Preload {
  constructor(elem) {
    this._elem  = elem
    this._elems = elem.querySelectorAll('[data-preload]:not([data-preload="loaded"])')
  }
  onInit() {
    this._elems.forEach(el => {
      const data = el.getAttribute('data-preload')
      let src = data
      if (data.includes('{')) {
        const json = JSON.parse(data)
        const sw   = screen.width
        src = sw >= 769 ? (json[769] ?? data) : sw >= 480 ? (json[480] ?? data) : (json[Object.keys(json).pop()] ?? data)
      }
      const img = new Image()
      img.onload = () => {
        if (el.nodeName === 'SOURCE')    el.setAttribute('srcset', src)
        else if (el.nodeName === 'IMG')  el.src = src
        else                             el.style.backgroundImage = `url(${src})`
        el.setAttribute('data-preload', 'loaded')
        el.dispatchEvent(new Event('preload-complete'))
      }
      img.src = src
    })
  }
}

// ─── Select (Choices.js) ─────────────────────────────────────────────────────
class Select {
  constructor(elem) {
    this._elem    = elem
    this._options = {
      searchEnabled: false, removeItems: false, paste: false,
      itemSelectText: '', shouldSort: false, link: false,
      ...JSON.parse(elem.getAttribute('data-select') || '{}')
    }
  }
  onInit() {
    if (!window.Choices) return
    const selectEl = this._options.elem || this._elem
    const link     = this._options.link
    const choicesOptions = { ...this._options }
    delete choicesOptions.link
    delete choicesOptions.elem
    new window.Choices(selectEl, choicesOptions)
    if (link) {
      this._elem.addEventListener('change', () => window.location.replace(this._elem.value))
    }
  }
}

// ─── Swiper component ─────────────────────────────────────────────────────────
class SwiperSlider {
  constructor(elem) {
    this._elem    = elem
    this._options = {
      slidesPerView: 1, pagination: false, loop: false, dontInit: false,
      navigation: false, autoplay: false, scrollbar: false,
      effect: false, a11y: true, imgHeight: false,
      observer: true, observeParents: true,
      roundLengths: true,
      ...JSON.parse(elem.getAttribute('data-swiper') || '{}')
    }
  }
  onInit() {
    if (this._options.dontInit || !window.Swiper) return
    new window.Swiper(this._elem, this._options)
    if (this._options.imgHeight) {
      this._setImageHeight()
      window.addEventListener('resize', () => this._setImageHeight())
    }
  }
  _setImageHeight() {
    let maxH = 0
    this._elem.querySelectorAll('img').forEach(img => {
      if (img.offsetHeight > maxH) {
        maxH = img.offsetHeight
        this._elem.parentNode?.querySelectorAll('.slider-arrow').forEach(a => {
          a.style.height = maxH + 'px'
        })
      }
    })
  }
}

// ─── ScrollHideNav ────────────────────────────────────────────────────────────
// Hides the site header on scroll-down, reveals it on scroll-up.
// Gated by GrowthBook feature flag 'scroll-hide-nav' — create this flag in the
// GrowthBook dashboard to activate for an A/B test or full rollout.
class ScrollHideNav {
  constructor() {
    this._header  = document.querySelector('.site-header')
    this._lastY   = window.scrollY
    this._hidden  = false
    this._ticking = false
    this._peakY   = window.scrollY
  }
  onInit() {
    if (!this._header) return
    const setHeaderH = () => {
      const h = this._header.offsetHeight
      document.documentElement.style.setProperty('--site-header-h', h + 'px')
      if (!this._hidden) document.documentElement.style.setProperty('--sd-header-h', h + 'px')
    }
    setHeaderH()
    window.addEventListener('resize', setHeaderH)
    if (window.ResizeObserver) new ResizeObserver(setHeaderH).observe(this._header)
    // Set initial state in case the page loads mid-scroll (e.g. browser scroll restore)
    this._header.classList.toggle('sticky', window.scrollY > 0)
    window.addEventListener('scroll', () => {
      if (this._ticking) return
      this._ticking = true
      requestAnimationFrame(() => { this._update(); this._ticking = false })
    }, { passive: true })
  }
  _setHidden(hide) {
    if (hide === this._hidden) return
    this._hidden = hide
    this._peakY  = window.scrollY  // reset high-water mark at each transition
    // Only the class changes — CSS owns the transform animation.
    this._header.classList.toggle('nav-hidden', hide)
    document.documentElement.style.setProperty(
      '--sd-header-h', hide ? '0px' : this._header.offsetHeight + 'px'
    )
  }
  _update() {
    const y     = window.scrollY
    const delta = y - this._lastY
    this._lastY = y
    // Track the furthest-down position reached since the last transition.
    if (y > this._peakY) this._peakY = y
    // Toggle 'sticky' class to switch overlay (homepage transparent) header to solid.
    // This was previously managed by hcSticky; we own it now.
    this._header.classList.toggle('sticky', y > 0)
    if (y < this._header.offsetHeight) {
      this._setHidden(false)
    } else if (delta > 4) {
      this._setHidden(true)
    } else if (this._hidden && (this._peakY - y) > 100) {
      this._setHidden(false) // user has scrolled 100px up from their deepest point
    }
  }
}

// TODO: re-gate behind GrowthBook flag 'scroll-hide-nav' once testing is done.
// ;(function () {
//   let _done = false
//   function tryInit() {
//     if (_done) return
//     if (!window._growthbook?.isOn('scroll-hide-nav')) return
//     _done = true
//     new ScrollHideNav().onInit()
//   }
//   document.addEventListener('DOMContentLoaded', tryInit)
//   window.addEventListener('load', tryInit)
// }())
document.addEventListener('DOMContentLoaded', () => new ScrollHideNav().onInit())

// ─── PredictiveSearch ────────────────────────────────────────────────────────
class PredictiveSearch {
  constructor() {
    this._input    = document.getElementById('HeaderSearch')
    this._form     = this._input?.closest('form')
    this._dropdown = null
    this._timer    = null
    this._query    = ''
  }
  onInit() {
    if (!this._input || !this._form) return
    this._dropdown = this._buildDropdown()
    this._form.style.position = 'relative'
    this._form.appendChild(this._dropdown)

    this._input.addEventListener('input', () => {
      clearTimeout(this._timer)
      const q = this._input.value.trim()
      if (!q) { this._hide(); return }
      this._timer = setTimeout(() => this._fetch(q), 220)
    })
    this._input.addEventListener('focus', () => {
      if (this._query && this._dropdown.children.length) this._show()
    })
    document.addEventListener('click', e => {
      if (!this._form.contains(e.target)) this._hide()
    })
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this._hide()
    })
  }
  _buildDropdown() {
    const el = document.createElement('div')
    el.className = 'search__predictive'
    el.setAttribute('role', 'listbox')
    return el
  }
  // Runs native predictive search and our own engine in parallel - the
  // engine covers colour/category/merchandising/informational, which
  // native predictive search has no concept of; native covers product
  // names, which the engine deliberately leaves alone (see
  // os-search-engine.js). Same dropdown, not a second one.
  _fetch(q) {
    this._query = q
    // Just products - Suggested (our engine) covers collection/page-type
    // destinations with better precision than native's fuzzy title match,
    // so native's own collection/page results are redundant here.
    const native = fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`)
      .then(r => r.json())
      .then(data => data?.resources?.results?.products ?? [])
      .catch(() => [])
    const suggestionsPromise = window.OSSearchEngine
      ? window.OSSearchEngine.suggest(q).catch(() => [])
      : Promise.resolve([])
    Promise.all([native, suggestionsPromise]).then(([products, suggestions]) => {
      this._render(q, products, suggestions)
    })
  }
  _formatPrice(price) {
    const symbol = { GBP: '£', USD: '$', EUR: '€' }[window.Shopify?.currency?.active] || '£'
    const n = parseFloat(price)
    return `${symbol}${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`
  }
  _renderSection(label, items, buildItem) {
    if (!items.length) return
    const heading = document.createElement('p')
    heading.className = 'search__predictive-heading'
    heading.textContent = label
    this._dropdown.appendChild(heading)
    items.forEach(item => this._dropdown.appendChild(buildItem(item)))
  }
  // Suggested entries are a curated shortlist, not a results list - laid
  // out as a wrapped row of compact chips rather than full item rows, so
  // they don't compete with Products for the dropdown's limited height.
  _renderSuggestions(suggestions) {
    if (!suggestions.length) return
    const heading = document.createElement('p')
    heading.className = 'search__predictive-heading'
    heading.textContent = 'Suggested'
    this._dropdown.appendChild(heading)

    const row = document.createElement('div')
    row.className = 'search__predictive-suggestions'
    suggestions.forEach(s => {
      const a = document.createElement('a')
      a.href = s.url
      a.className = 'search__predictive-chip'
      a.setAttribute('role', 'option')
      a.textContent = s.label
      row.appendChild(a)
    })
    this._dropdown.appendChild(row)
  }
  _makeItem(href, imgUrl, title, sub) {
    const a = document.createElement('a')
    a.href = href
    a.className = 'search__predictive-item'
    a.setAttribute('role', 'option')
    a.innerHTML = `
      ${imgUrl ? `<img class="search__predictive-img" src="${imgUrl}" alt="" width="48" height="48">` : '<span class="search__predictive-img-placeholder"></span>'}
      <span class="search__predictive-info">
        <span class="search__predictive-title">${title}</span>
        ${sub ? `<span class="search__predictive-price">${sub}</span>` : ''}
      </span>`
    return a
  }
  _render(q, products, suggestions) {
    if (this._query !== q) return
    this._dropdown.innerHTML = ''
    const hasAny = products.length || suggestions.length
    if (!hasAny) {
      this._dropdown.innerHTML = `<p class="search__predictive-empty">No results for &ldquo;${q}&rdquo;</p>`
      this._show()
      return
    }
    this._renderSuggestions(suggestions)
    this._renderSection('Products', products.slice(0, 5), p => {
      const img   = p.featured_image?.url ?? ''
      const price = p.price != null ? this._formatPrice(p.price) : ''
      return this._makeItem(p.url, img, p.title, price)
    })
    const footer = document.createElement('div')
    footer.className = 'search__predictive-footer'
    footer.innerHTML = `<a href="/search?type=product&q=${encodeURIComponent(q)}" class="search__predictive-all">View all results for &ldquo;${q}&rdquo;</a>`
    this._dropdown.appendChild(footer)
    this._show()
  }
  _show() { this._dropdown.classList.add('is-visible') }
  _hide() { this._dropdown.classList.remove('is-visible') }
}

document.addEventListener('DOMContentLoaded', () => new PredictiveSearch().onInit())

// ─── Component registry & init ────────────────────────────────────────────────
const _componentMap = {
  'change-class':    el => new ChangeClass(el),
  'cart-count':      el => new CartCount(el),
  'currency-switch': el => new Currency(el),
  'in-viewport':     el => new InViewport(el),
  'scroll-to':       el => new ScrollTo(el),
  'sticky':          el => new Sticky(el),
  'slide':           el => new Slide(el),
  'video':           el => new Video(el),
  'preloader':       el => new Preload(el),
  'select':          el => new Select(el),
  'swiper':          el => new SwiperSlider(el)
}

window.initComponents = (container = document) => {
  Object.entries(_componentMap).forEach(([key, factory]) => {
    container.querySelectorAll(`.js-component__${key}, [data-${key}]`).forEach(el => {
      const flag = `_os_${key.replace(/-/g, '_')}_init`
      if (el[flag]) return
      el[flag] = true
      factory(el).onInit()
    })
  })
}

document.addEventListener('DOMContentLoaded', () => window.initComponents())

// Resolves via the vocabulary-driven SearchEngine (os-search-engine.js).
// Matomo's site-search tracking isn't a separate call - it's the ordinary
// trackPageView (theme.liquid) getting auto-classified as a search by
// Matomo's own Site Search config, based on the destination URL carrying
// the configured "q" parameter. That still works untouched for the
// unresolved case (falls through to a real /search?q=... load). It's the
// resolved case that needs an explicit trackSiteSearch call here, since
// the destination we redirect to (a collection, a page) has no "q" param
// for Matomo to recognise - without this, that search would go untracked
// entirely rather than double-counted.
document.querySelectorAll('form[action*="/search"]').forEach(form => {
  form.addEventListener('submit', e => {
    const q = (form.querySelector('[name="q"]')?.value || '').trim()
    if (!q || !window.OSSearchEngine) return
    e.preventDefault()
    window.OSSearchEngine.resolve(q).then(result => {
      if (result) {
        window._paq = window._paq || []
        window._paq.push(['trackSiteSearch', q, result.method])
        window.location.href = result.url
      } else {
        form.submit()
      }
    })
  })
})
